import { applyCastAliasCleanupPass } from "@/lib/castAliasCleanupPass";
import type { BookCastJson, CastCharacter, CastNarrator, CastVoiceAssignment } from "@/lib/castAnalysisTypes";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function str(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

function num01(v: unknown): number | undefined {
  if (typeof v !== "number" || !Number.isFinite(v)) return undefined;
  return Math.min(1, Math.max(0, v));
}

function normalizeVoice(raw: unknown): CastVoiceAssignment {
  if (raw === null || raw === undefined) return null;
  if (!isRecord(raw)) return null;
  const voice_id = raw.voice_id === null ? null : str(raw.voice_id) || null;
  const voice_name = raw.voice_name === null ? null : str(raw.voice_name) || null;
  const reason = raw.reason === null ? null : str(raw.reason) || null;
  let voice_settings:
    | {
        stability?: number;
        similarity_boost?: number;
        style?: number;
        use_speaker_boost?: boolean;
      }
    | undefined;
  if (isRecord(raw.voice_settings)) {
    const vs = raw.voice_settings;
    voice_settings = {
      stability: num01(vs.stability),
      similarity_boost: num01(vs.similarity_boost),
      style: num01(vs.style),
      use_speaker_boost: typeof vs.use_speaker_boost === "boolean" ? vs.use_speaker_boost : undefined,
    };
  }
  if (!voice_id && !voice_name && !reason && !voice_settings) return null;
  return { voice_id, voice_name, reason, voice_settings };
}

export function normalizeNarrator(raw: unknown): CastNarrator {
  if (!isRecord(raw)) {
    return { name: "Narrator", description: "", tone: "", voice_direction: "" };
  }
  return {
    name: str(raw.name) || "Narrator",
    description: str(raw.description),
    tone: str(raw.tone),
    voice_direction: str(raw.voice_direction),
  };
}

export function normalizeCharacter(raw: unknown, index: number): CastCharacter | null {
  if (!isRecord(raw)) return null;
  const name = str(raw.name);
  if (!name) return null;
  let relationships: string[] = [];
  if (Array.isArray(raw.relationships)) {
    relationships = raw.relationships.map((x) => str(x)).filter((s) => s.length > 0);
  }
  let confidence: number | undefined;
  if (typeof raw.confidence === "number" && Number.isFinite(raw.confidence)) {
    confidence = Math.min(1, Math.max(0, raw.confidence));
  }
  return {
    name,
    role: str(raw.role) || (index === 0 ? "character" : ""),
    description: str(raw.description),
    backstory: str(raw.backstory),
    relationships,
    personality: str(raw.personality),
    age_or_vocal_quality: str(raw.age_or_vocal_quality),
    speaking_style: str(raw.speaking_style),
    voice_direction: str(raw.voice_direction),
    confidence,
    voice: normalizeVoice(raw.voice),
  };
}

/** Build a persisted artifact from the n8n webhook JSON (tolerant of missing optional fields). */
export function normalizeBookCastFromWebhook(
  payload: unknown,
  meta: { bookId: string; title: string; author: string; source?: string },
): BookCastJson {
  const base = {
    book_id: meta.bookId,
    title: meta.title,
    author: meta.author,
    source: meta.source,
  };
  if (!isRecord(payload) || payload.success === false) {
    const err =
      isRecord(payload) && str(payload.error)
        ? str(payload.error)
        : "Cast analysis failed";
    return {
      success: false,
      ...base,
      status: "error",
      narrator: { name: "Narrator", description: "", tone: "", voice_direction: "" },
      characters: [],
      error: err,
    };
  }

  const narrator = normalizeNarrator(payload.narrator);
  const rawChars = Array.isArray(payload.characters) ? payload.characters : [];
  const characters: CastCharacter[] = [];
  rawChars.forEach((c, i) => {
    const row = normalizeCharacter(c, i);
    if (row) characters.push(row);
  });

  const title = str(payload.title) || meta.title;
  const author = str(payload.author) || meta.author;

  return applyCastAliasCleanupPass({
    success: true,
    book_id: str(payload.book_id) || meta.bookId,
    title,
    author,
    source: meta.source,
    status: "ready",
    narrator,
    characters,
    analysis_mode: "excerpt",
    chunks_analyzed: 1,
  });
}
